import React, { useEffect, useState, useRef } from "react";
import { CLASSES, OTHER_CATEGORIES } from "./data";

const API_BASE = process.env.NEXT_PUBLIC_BASE_URL;

export default function NewsEditor({ item = null, visible, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [klass, setKlass] = useState("All");
  const [category, setCategory] = useState("Other");
  const [date, setDate] = useState("");
  const [images, setImages] = useState([]);
  const [originalImages, setOriginalImages] = useState([]);
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setTitle(item.title || "");
      setContent(item.content || item.description || "");
      setKlass(item.class || "All");
      setCategory(item.category?.name || item.category || "Other");
      setDate(
        item.date ||
          (item.createdAt
            ? new Date(item.createdAt).toISOString().slice(0, 10)
            : new Date().toISOString().slice(0, 10)),
      );
      const imgs = (item.images || item.photos || []).map((src) => ({ src }));
      setImages(imgs);
      setOriginalImages((item.images || item.photos || []).slice());
    } else if (visible) {
      setTitle("");
      setContent("");
      setKlass("All");
      setCategory("Other");
      setDate(new Date().toISOString().slice(0, 10));
      setImages([]);
      setOriginalImages([]);
    }
  }, [item, visible]);

  if (!visible) return null;

  function handleFile(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () =>
        setImages((s) => [...s, { name: f.name, src: reader.result, file: f }]);
      reader.readAsDataURL(f);
    });
    e.target.value = null;
  }

  function removeImage(idx) {
    setImages((s) => s.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim() || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    try {
      const form = new FormData();
      form.append("title", title.trim());
      form.append("description", content.trim());
      form.append("class", klass);
      form.append("category", category);
      form.append("date", date);

      // append new files
      images.forEach((img) => {
        if (img.file) form.append("photos", img.file);
      });

      // if editing, compute removed photos
      if (item && originalImages.length) {
        const currentUrls = images
          .filter((im) => im.src && !im.file)
          .map((im) => im.src);
        const removed = originalImages.filter((u) => !currentUrls.includes(u));
        if (removed.length)
          form.append("removePhotos", JSON.stringify(removed));
      }

      // Pass the FormData to parent to handle the network request.
      // Parent should resolve when save is complete.
      await onSave(form, item);
    } catch (err) {
      console.error("Save error:", err);
      alert("Error saving news. Check console.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center px-4 py-6">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-xl mx-auto">
        <form
          onSubmit={handleSubmit}
          role="dialog"
          aria-modal="true"
          aria-labelledby="news-editor-title"
          className="bg-white rounded-lg shadow-xl p-6 max-h-[85vh] overflow-auto transform transition-all duration-200 ease-out"
        >
          <header className="flex items-center justify-between mb-4">
            <h3 id="news-editor-title" className="text-lg font-semibold">
              {item ? "Edit Event" : "Create Event"}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Class</label>
              <select
                value={klass}
                onChange={(e) => setKlass(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded"
              >
                {CLASSES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded"
              >
                {OTHER_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium">Description</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              className="mt-1 w-full px-3 py-2 border rounded"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium">
              Images (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFile}
              className="mt-2"
            />

            {images.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {images.map((im, idx) => (
                  <div
                    key={idx}
                    className="relative w-24 h-24 rounded overflow-hidden border"
                  >
                    <img
                      src={`${API_BASE}${im}`}
                      alt={im.name || `img-${idx}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-1 right-1 bg-black/40 text-white rounded px-1 text-xs"
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <footer className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 border rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#1e81b0] text-white rounded"
              disabled={!title.trim() || saving}
            >
              {item ? "Save" : "Create"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
